import type {
  DomainTaskStatusType,
  DtoAdminGetApplicationResponse,
  DtoAdminLoanSummary,
  DtoAdminSearchBorrowerApplication,
  DtoCondition,
  DtoConditionTag,
  DtoCreateConditionRequest,
} from "@porchpass/api-client";
import type {
  ConditionSection,
  PreApprovalCondition,
  PreApprovalNote,
} from "./types";
import type { DtoNote } from "@porchpass/api-client";

export type { DtoConditionTag };

export function mapApiConditionTag(
  tag: DtoConditionTag | undefined,
): DtoConditionTag {
  return tag ?? "customer";
}

export function mapTagToSection(tag: DtoConditionTag): ConditionSection {
  switch (tag) {
    case "funding":
      return "funding";
    case "seller":
      return "seller";
    case "customer":
    case "customer_if_needed":
    default:
      return "customer";
  }
}

export function mapSectionToTag(
  section: ConditionSection,
  optional = false,
): DtoConditionTag {
  if (optional) {
    return "customer_if_needed";
  }
  return section;
}

export function mapDtoCondition(
  externalId: string,
  condition: DtoCondition,
  digifiSynced = true,
): PreApprovalCondition {
  const taskId = condition.id?.trim();
  if (!taskId) {
    throw new Error("Condition is missing a DigiFi task ID.");
  }

  return {
    ...condition,
    id: taskId,
    application_id: externalId,
    section: mapTagToSection(mapApiConditionTag(condition.tag)),
    title: condition.title?.trim() || "Untitled condition",
    internal_description: condition.internal_description?.trim() || undefined,
    external_description: condition.external_description?.trim() || undefined,
    digifi_synced: digifiSynced,
  };
}

export function mapDtoNote(
  externalId: string,
  dto: DtoNote,
  digifiSynced = true,
): PreApprovalNote {
  return {
    ...dto,
    id: dto.id ?? "",
    application_id: externalId,
    author: dto.author ?? "Unknown",
    note: dto.note ?? "",
    created_at: dto.created_at ?? new Date().toISOString(),
    digifi_synced: digifiSynced,
  };
}

function valueOrFallback(
  value: string | undefined,
  fallback = "Not available",
) {
  const trimmed = value?.trim();
  return trimmed || fallback;
}

export function applicationExternalId(
  application: DtoAdminSearchBorrowerApplication,
): string | null {
  return application.external_id?.trim() || null;
}

export function applicationLosId(
  application: DtoAdminSearchBorrowerApplication,
): string | null {
  return application.application_id?.trim() || null;
}

export function applicationDisplayName(
  application: DtoAdminSearchBorrowerApplication,
): string {
  return valueOrFallback(application.borrower?.full_name, "Unnamed borrower");
}

export function applicationPhone(
  application: DtoAdminSearchBorrowerApplication,
): string {
  return valueOrFallback(application.borrower?.phone_number);
}

export function applicationEmail(
  application: DtoAdminSearchBorrowerApplication,
): string {
  return valueOrFallback(application.borrower?.email);
}

export function applicationAddress(
  application: DtoAdminSearchBorrowerApplication,
): string {
  return valueOrFallback(application.borrower?.address);
}

export function applicationStatus(
  application: DtoAdminSearchBorrowerApplication,
): string {
  return valueOrFallback(application.application_status, "Unknown status");
}

export function mapBorrowerSearchResult(
  application: DtoAdminSearchBorrowerApplication,
): DtoAdminSearchBorrowerApplication | null {
  if (!applicationExternalId(application) || !applicationLosId(application)) {
    return null;
  }
  return application;
}

export function mapLoanSummary(
  loan?: DtoAdminLoanSummary,
): DtoAdminLoanSummary | null {
  const loanAmount = loan?.loan_amount;
  const interestRate = loan?.interest_rate;
  const termMonths = loan?.term_months;
  if (
    loanAmount == null ||
    interestRate == null ||
    termMonths == null ||
    loanAmount <= 0 ||
    interestRate <= 0 ||
    termMonths <= 0
  ) {
    return null;
  }

  return {
    loan_amount: loanAmount,
    interest_rate: interestRate,
    term_months: termMonths,
  };
}

export function mapWorkspaceBootstrap(
  response: DtoAdminGetApplicationResponse,
): DtoAdminGetApplicationResponse {
  const externalId = response.external_id?.trim();
  const losApplicationId = response.los_application_id?.trim();
  if (!externalId || !losApplicationId) {
    throw new Error("Application lookup returned incomplete identifiers.");
  }

  return {
    ...response,
    external_id: externalId,
    los_application_id: losApplicationId,
    application_status: valueOrFallback(
      response.application_status,
      "Unknown status",
    ),
    borrower: response.borrower
      ? {
          ...response.borrower,
          full_name: valueOrFallback(
            response.borrower.full_name,
            "Unnamed borrower",
          ),
          phone_number: valueOrFallback(response.borrower.phone_number),
          email: valueOrFallback(response.borrower.email),
          address: valueOrFallback(response.borrower.address),
        }
      : response.borrower,
    loan: mapLoanSummary(response.loan) ?? undefined,
  };
}

export function buildCreateConditionBody(
  externalId: string,
  section: ConditionSection,
  payload: Pick<
    DtoCreateConditionRequest,
    "title" | "internal_description" | "external_description"
  >,
): DtoCreateConditionRequest {
  const internalDescription = payload.internal_description?.trim();
  const externalDescription = payload.external_description?.trim();
  return {
    application_id: externalId,
    title: payload.title,
    tag: mapSectionToTag(section),
    status: "not_done",
    ...(internalDescription
      ? { internal_description: internalDescription }
      : {}),
    ...(externalDescription
      ? { external_description: externalDescription }
      : {}),
  };
}

export function filterMatchesStatus(
  status: DomainTaskStatusType | undefined,
  filter: import("./types").ConditionFilter,
): boolean {
  if (filter === "all") {
    return true;
  }
  return status === filter;
}
